import type { ICharacter } from './character.interface';
import type { IChatLayout } from './chats.interface';
import type { ELanguage } from './language.interface';
import type { Model } from './models.interface';
import type { IPersona, IPersonaWithMessage } from './persona.interface';
import type { ICreatorShort } from './prompts.interface';
import type { EMessageRatingReasons } from './tell-us-more.interface';

/** Возможные типы видимости чата */
export type TChatVisibility = 'PUBLIC' | 'PRIVATE';

/** Возможный статус закрепления чата */
export type TPinnedStatus = boolean;

/** Тип отправителя сообщения */
export enum ESendType {
  CHARACTER = 'CHARACTER',
  SYSTEM = 'SYSTEM',
  USER = 'USER',
}

export const DEFAULT_LAYOUT: IChatLayout = {
  [ESendType.SYSTEM]: 'LEFT',
  [ESendType.CHARACTER]: 'LEFT',
  [ESendType.USER]: 'RIGHT',
};

export enum EPlaceholderReason {
  PERSONA,
  CHAT,
  MODEL,
  SUBSCRIBE,
  FUNDS,
}

export interface IChatInputPlaceholderConfig {
  reason: EPlaceholderReason;
  text: string;
  secondaryText?: string;
  textForBtn: string;
  //text for optional second btn
  secondaryBtnText?: string;
}

/** Сообщение внутри чата */
export interface IChatMessage {
  /** Альтернативные версии сообщения */
  alternativeVersions: IAlternativeMessage[];
  /** Айди чата **/
  chatId?: 'string';
  /** Текст сообщения */
  content: string;
  debugInfo?: string | null;
  /** Идентификатор сообщения */
  id: string;
  /** Список URL картинок, прикреплённых к сообщению */
  images: IChatMessageImage[];
  /** Список URL видео, прикреплённых к сообщению */
  videos?: IChatMessageVideo[];
  /** Является ли началом новой ветки */
  isBranchStart: boolean;
  /** Идентификатор родительского сообщения (если является веткой) */
  parentMessageId: string | null;
  /** Айди промпта **/
  promptId?: null;
  /** Кол-во токенов запроса **/
  requestTokens?: null;
  /** Кол-во токенов ответа **/
  responseTokens?: null;
  /** Тип отправителя */
  sendType: ESendType;
  /** Идентификатор пользователя-отправителя */
  sendUserId: string;
  /** Стоимость сообщения **/
  spentCents?: null;
  /** Идентификатор хода (turn) */
  turnId: string;
  /** Время обновления **/
  updatedAt?: '2025-07-31T12:10:28.877Z';
  /** Индекс версии сообщения */
  versionIndex: number;
  /** Дата создания */
  createdAt: string;
  /** Оценка сообщения **/
  rating?: IMessageRating;
  /** Persona **/
  persona?: IPersona;
  /** Is chat password applied */
  isDecrypted?: boolean;
  /** Если модель не смогла сгенерировать сообщение */
  isMessageEmpty?: boolean;
  /** Если контент содержит только пробельные символы */
  isWhitespaceOnly?: boolean;
  /** Если сообщение находится в стадии генерации */
  isBeingGenerated?: boolean;
}

export interface IAlternativeMessage {
  content: string;
  debugInfo?: string | null;
  id: string;
  images: IChatMessageImage[];
  isBranchStart: boolean;
  language?: ELanguage;
  title?: string;
  versionIndex: number;
  videos?: IChatMessageVideo[];
}

export interface IChatMessageVideo {
  readonly videoUrl: string;
  readonly status: 'COMPLETED' | 'PENDING' | 'FAILED' | 'PROCESSING';
  readonly thumbnailUrl?: string;
}

export interface IChatMessageImage {
  createdAt: string;
  id: string;
  messageId: string;
  status: 'COMPLETED' | 'PENDING' | 'FAILED' | 'PROCESSING';
  url: string;
  isDeleted: boolean;
}

/** Последний использованный системный промпт в чате */
export interface IChatPrompt {
  /** Идентификатор промпта */
  id: string;
  /** Идентификатор пользователя */
  userId: string;
  /** Название промпта */
  title: string;
  /** Описание промпта */
  description: string;
  /** Идентификатор корневого промпта */
  rootPromptId: string;
  /** Идентификатор предыдущей версии */
  previousVersionId: string | null;
  /** Номер версии */
  versionNumber: number;
  /** Является ли промпт одобрённым */
  isApproved: boolean;
  /** Является ли промпт последним публичным */
  isLatestPublic: boolean;
  /** Журнал изменений */
  changelog: string;
  /** Причина модерации */
  moderationReason: string | null;
  /** Текст промпта */
  promptText: string;
  /** Количество токенов */
  tokenCount: number;
  /** Видимость */
  visibility: TChatVisibility;
  /** Статус скрытия */
  hideStatus: 'VISIBLE' | 'HIDDEN';
  /** Количество использований */
  usageCount: number;
  /** Дата создания */
  createdAt: string;
  /** Дата обновления */
  updatedAt: string;
  /** Дата удаления */
  deletedAt: string | null;
  /** Вес промпта */
  weight: number;
  /** Создатель промпта */
  creator: ICreatorShort;
}

/** Последняя использованная модель в чате */
export interface IChatModel {
  /** Идентификатор модели */
  id: string;
  /** Название модели */
  modelName: string;
  /** Название */
  name: string;
  /** Провайдер */
  provider: string;
  /** Описание */
  description: string;
  /** Размер контекста */
  contextSize: number;
  /** Цена входа */
  inputPrice: number;
  /** Цена выхода */
  outputPrice: number;
  /** Цена для пользователя */
  priceToShow: number;
  /** Возможности */
  capabilities: string[];
  /** Является ли модель по умолчанию */
  isDefault: boolean;
  /** Является ли модель скрытой */
  isHidden: boolean;
  /** Идентификатор хозяина */
  hostId: string | null;
  /** Является ли модель пользовательской */
  isCustom: boolean;
  /** Дата удаления */
  deletedAt: string | null;
  /** Дата последнего тестирования */
  lastTestedAt: string | null;
  /** Дата создания */
  createdAt: string;
  /** Дата обновления */
  updatedAt: string;
  /** Максимальное количество токенов */
  maxContextTokens: number;
  /** Провайдер OpenRouter */
  openRouterProvider: string | null;
  /** Требуемый тарифный план */
  requiredSubscriptionTier: string | null;
  /** Поддержка TopK */
  supportTopK: boolean;
  /** Поддержка TopP */
  supportPresencePenalty: boolean;
  /** Поддержка частоты */
  supportFrequencyPenalty: boolean;
  /** Поддержка максимального количества токенов */
  supportMaxTokens: boolean;
  /** Количество нейронных связей */
  memory: number;
  /** Рекомендуется к использованию */
  isRecommended: boolean;
  /** Является созданным пользователем пресетом */
  isPreset: boolean;
  /** Текущие настройки модели */
  generationSettings: IChatModelSettings[];
}

export interface IChatModelSettings {
  key: keyof typeof EChatModelSettings;
  label: EChatModelSettings;
  minValue: number;
  maxValue: number;
  currentValue: number;
}

export enum EChatModelSettings {
  TEMPERATURE = 'Temperature',
  TOP_K = 'Top-K',
  TOP_P = 'Top-P',
  TOTAL_CONTEXT_TOKENS = 'Total Context Tokens',
  MAX_RESPONSE_TOKENS = 'Max Response Tokens',
  PRESENCE_PENALTY = 'Presence Penalty',
  FREQUENCY_PENALTY = 'Frequency Penalty',
}

export interface IChat {
  id: string;
  title: string;
  createdAt: string; // ISO-строка даты
  deletedAt: string; // ISO-строка даты
  updatedAt: string;
  userId: string;
  userName: string;
  userAvatar: string;
  viewCount: number;
  likeCount: number;
  favoriteCount: number;
  shareCount: number;
  totalEmojiCount: number;
  totalMemories: number;
  visibility: 'PRIVATE' | 'PUBLIC';
  pinnedStatus: boolean;
  like: boolean;
  favorite: boolean;
  characterId: string;
  personaId: string | null;
  character: ICharacter;
  /** messages count from response */
  messageCount: number;
  /** total messages in chat with bot*/
  totalMessageCount: number;
  lastUsedPromptId: string | null;
  lastUsedModelId: string | null;
  lastUsedPrompt: IChatPrompt | null;
  lastUsedModel: Model | null;
  isEncrypted: boolean;
  avatarUrl: string;
  userSlug: string;
  userProfileUrl: string;
  lastMessage: string;
  lastMessageAt: string;
  lastUsedTemperature: number | null;
  lastUsedTopP: number | null;
  lastUsedTopK: number | null;
  lastUsedPresencePenalty: number | null;
  lastUsedFrequencyPenalty: number | null;
  lastUsedMaxTokens: number | null;
  lastUsedTotalTokens: number | null;
  chatBackground: string | null;
  savedModelPrompts?: ISavedModelPrompt[];
}

/** Основной интерфейс данных чата */
export interface IChatWithDetails extends IChat {
  messages: IChatMessage[];
  persona: IPersona | null;
  personas: IPersonaWithMessage[];
}

export interface IChatPromptWithDetails extends IChatPrompt {
  modelSettings: IModelSettings[];
}

export interface IModelSettings {
  id: string;
  modelId: string;
  modelName: string;
  modelProvider: string;
  temperature: number;
  topP: number;
  topK: number;
  presencePenalty: number;
  frequencyPenalty: number;
  maxTokens: number;
  totalTokens?: number;
  isDefault: boolean;
}

export interface IChatPatchBody {
  title?: string;
  visibility?: 'PRIVATE' | 'PUBLIC';
  personaId?: string;
  modelId?: string;
  temperature?: number;
  topP?: number;
  topK?: number;
  presencePenalty?: number;
  frequencyPenalty?: number;
  maxTokens?: number;
  totalTokens?: number;
  promptId?: string;
  isPreset?: boolean;
}

export interface IMessageRating {
  ratingValue?: number;
  ratingReasons?: EMessageRatingReasons[];
  ratingFeedback?: string;
}

export interface ISavedModelPrompt {
  model: Model;
  prompt: IChatPrompt;
  isPreset: boolean;
  temperature?: number;
  topP?: number;
  topK?: number;
  totalTokens?: number;
  maxTokens?: number;
  presencePenalty?: number;
  frequencyPenalty?: number;
}

export interface ICurrentModelPromptIds {
  model: string;
  prompt: string;
}

export enum EChatAction {
  DELETE = 'COMMON.DELETE',
  DELETE_IMAGE = 'BLOCKS.BLOCK_BUTTON.DELETE_IMAGE',
  EDIT = 'BLOCKS.BLOCK_BUTTON.EDIT',
  COPY = 'COMMON.COPY',
  VOICE_OVER = 'BLOCKS.BLOCK_BUTTON.VOICE_OVER',
  GENERATE_IMAGE = 'COMMON.GENERATE',
  REGENERATE = 'COMMON.REGENERATE',
  REGENERATE_MESSAGE = 'BLOCKS.BLOCK_BUTTON.REGENERATE_MESSAGE',
  NEW_CHAT_FROM_HERE = 'BLOCKS.BLOCK_BUTTON.NEW_CHAT_FROM_HERE',
  COMPLAIN = 'MODALS.COMPLAIN.TITLE',
  IMAGE_SETTINGS = 'BLOCKS.BLOCK_BUTTON.IMAGE_SETTINGS',
  RATE_MESSAGE = 'BLOCKS.BLOCK_BUTTON.RATE_MESSAGE',
}
